/* eslint-disable max-len */
import { useEffect, useState } from "react";
import styled from "styled-components";
import dayjs from "dayjs";
import { useAuth } from "../../modules/auth/useAuth";
import { useTranslation } from "react-i18next";
import { Modal, Button, Spinner } from "@xfrontend/xui-react-plus";
import getVersion from "#/apis/infra/getVersion";
import packageJson from "../../../package.json";

const CustomModal = styled(Modal)`
  .modal-content {
    background-color: var(--x-purple-500) !important;
  }
  .modal-body {
    display: flex;
    gap: 24px;
  }
`;

const LogoIcon = styled.div`
  width: 50px;
  height: 50px;
  flex-shrink: 0;
`;

const Content = styled.div`
  flex: 1;
`;

const ProductName = styled.div`
  font-size: 30px;
  font-weight: 700;
  color: var(--bs-gray-300);
`;

const DescList = styled.dl`
  display: inline;
  dt {
    float: left;
    width: 30%;
    font-size: 14px;
    font-weight: 400;
    color: var(--x-gray-200);
  }
  dd {
    width: 70%;
    font-size: 14px;
    font-weight: 400;
    color: var(--x-gray-200);
    font-family: var(--bs-font-monospace);
    display: flex;
    flex: 1;
  }
`;

const SubDescList = styled(DescList)`
  > dt {
    color: var(--bs-gray-500);
  }
  > dd {
    color: var(--bs-gray-200);
  }
`;

const Divider = styled.div`
  height: 1px;
  width: 100%;
  background-color: var(--bs-secondary);
  margin-bottom: 12px;
`;

type VersionViewerProps = {
  show: boolean;
  setShow: (v: boolean) => void;
};

const VersionViewer = ({ show, setShow }: VersionViewerProps): JSX.Element => {
  const auth = useAuth();
  const { t } = useTranslation("header");
  const [backendVersion, setBackendVersion] = useState<string>("");
  const [versionLoading, setVersionLoading] = useState(false);
  const onClose = () => setShow(false);

  useEffect(() => {
    if (show) {
      setVersionLoading(true);
      const token = auth.user?.access_token ?? "";
      getVersion({ token }).then((res) => {
        if (res.success && res.data) {
          setBackendVersion(res.data.version ?? "");
        }
      }).finally(() => {
        setVersionLoading(false);
      });
    }
  }, [show]);

  return (
    <CustomModal
      id="aboutModal"
      show={show}
      onClose={onClose}
      backdrop="static"
      onEscapeKeyDown={() => true}
    >
      <Modal.Body>
        <LogoIcon>
          <svg width="50" height="50" viewBox="0 0 36 32" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M20.7279 24.7551H15.4365L8.13086 16.0001L15.4365 7.24512H20.7279L28.0339 16.0001L20.7279 24.7551ZM16.8032 21.8214H19.3612L24.2191 16.0001L19.3612 10.1788H16.8032L11.9457 16.0001L16.8032 21.8214Z" fill="#C9BAFF" />
            <path d="M35.0493 14.6641H34.1564H30.5036H20.1144L18.8499 13.1486H17.3146L14.9353 16.0002L17.3146 18.8514H18.8499L20.157 17.2851H30.5463L21.6241 27.9769H14.5403L4.54574 16.0002L14.5403 4.02311H21.6241L28.4767 12.235H33.0224L22.813 0H13.3514L0 16.0002L13.3514 32H22.8127L36.1644 16.0002L35.0493 14.6641Z" fill="#846CE6" />
          </svg>
        </LogoIcon>
        <Content>
          <ProductName className="mb-1">{t("about.productName")}</ProductName>
          <div style={{
            fontSize: 14, fontWeight: 400, color: "var(--bs-gray-300)", marginBottom: 4,
          }}
          >
            {t("about.version", { version: packageJson.version })}
          </div>
          <DescList>
            <dt>{t("about.systemTime")}</dt>
            <dd>{dayjs().format("YYYY-MM-DD HH:mm:ss")}</dd>
          </DescList>
          <Divider />
          <SubDescList>
            <dt>{t("about.frontend")}</dt>
            <dd>{`${packageJson.version || "0.0.0"}${process.env.GIT_COMMIT_HASH ? ` (${process.env.GIT_COMMIT_HASH})` : ""}`}</dd>
            <dt>{t("about.backend")}</dt>
            <dd>
              {versionLoading
                ? <Spinner animation="border" size="sm" variant="primary" />
                : backendVersion || "0.0.0"}
            </dd>
          </SubDescList>
        </Content>
      </Modal.Body>
      <Modal.Footer className="justify-content-between">
        <small style={{ color: "var(--x-gray-500)" }}>
          {t("about.copyright", { year: dayjs().year() })}
        </small>
        <Button
          id="aboutModal-close"
          variant="secondary"
          text={t("about.close")}
          onClick={onClose}
        />
      </Modal.Footer>
    </CustomModal>
  );
};

export default VersionViewer;
